import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { meetingsAPI, userAPI } from '../../lib/api';
import { apiErrorText } from './meetingsUtils';

const ADMIN_SECTIONS = [['settings', 'Settings'], ['digest', 'Weekly email'], ['categories', 'Product categories'], ['visitors', 'Field visitors'], ['questions', 'Pointer questions'],
  ['prospects', 'Prospect matching'], ['reassign', 'Reassign']];

const WEEKLY_DIGEST_TZ_OPTIONS = ['Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata', 'Asia/Dubai',
  'Asia/Tokyo', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'UTC'];

export default function AdminTab() {
  const [section, setSection] = useState('settings');
  return (
    <div className="space-y-4" data-testid="meetings-admin-tab">
      <div className="flex flex-wrap gap-2">
        {ADMIN_SECTIONS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setSection(key)} data-testid={`admin-section-${key}`}
            className={`px-3 py-1.5 text-sm rounded-sm border ${section === key ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted/60'}`}>
            {label}
          </button>
        ))}
      </div>
      {section === 'settings' && <SettingsSection />}
      {section === 'digest' && <WeeklyDigestSection />}
      {section === 'categories' && <ProductCategoriesSection />}
      {section === 'visitors' && <VisitorsSection />}
      {section === 'questions' && <QuestionsSection />}
      {section === 'prospects' && <ProspectsSection />}
      {section === 'reassign' && <ReassignSection />}
    </div>
  );
}

function SettingsSection() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { meetingsAPI.adminGetSettings().then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!settings) return <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>;

  const field = (key, label, type = 'number') => (
    <div key={key}>
      <Label>{label}</Label>
      <Input type={type} value={settings[key] ?? ''} data-testid={`admin-setting-${key}`}
        onChange={(e) => setSettings({ ...settings, [key]: type === 'number' ? Number(e.target.value) : e.target.value })} />
    </div>
  );

  const save = async () => {
    setSaving(true);
    try {
      await meetingsAPI.adminUpdateSettings({
        monthly_target_default: settings.monthly_target_default, report_due_hours: settings.report_due_hours,
        backdate_days: settings.backdate_days, review_working_days: settings.review_working_days,
        min_summary_chars: settings.min_summary_chars, max_files: settings.max_files, max_file_mb: settings.max_file_mb,
      });
      toast.success('Settings saved');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to save settings')); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-4 space-y-4" data-testid="admin-settings-section">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {field('monthly_target_default', 'Default monthly target')}
        {field('report_due_hours', 'Report due (hours)')}
        {field('backdate_days', 'Backdate allowance (days)')}
        {field('review_working_days', 'Review SLA (working days)')}
        {field('min_summary_chars', 'Min summary characters')}
        {field('max_files', 'Max files per meeting')}
        {field('max_file_mb', 'Max file size (MB)')}
      </div>
      <Button size="sm" disabled={saving} onClick={save} data-testid="admin-settings-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save settings
      </Button>
    </CardContent></Card>
  );
}

// Part C11 (26 Oct 2026, review round 2C): on/off, day, time, timezone and the
// company-wide-copy picker for the weekly "Visits by person" digest.
function WeeklyDigestSection() {
  const [wd, setWd] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    meetingsAPI.adminGetSettings().then((r) => setWd(r.data.weekly_digest)).catch(() => setLoadError(true));
    meetingsAPI.adminWeeklyDigestCandidates().then((r) => setCandidates(r.data.candidates || [])).catch(() => setLoadError(true));
  }, []);
  if (loadError) return <p className="text-sm text-destructive" data-testid="weekly-digest-load-error">Failed to load weekly email settings. Please refresh.</p>;
  if (!wd) return <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>;

  const toggleRecipient = (id) => {
    const ids = wd.company_wide_recipient_ids || [];
    setWd({ ...wd, company_wide_recipient_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  };

  const save = async () => {
    if (!Number.isInteger(wd.hour) || wd.hour < 0 || wd.hour > 23) { toast.error('Hour must be 0-23'); return; }
    setSaving(true);
    try {
      await meetingsAPI.adminUpdateSettings({ weekly_digest: wd });
      toast.success('Weekly email settings saved');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to save')); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-4 space-y-4" data-testid="admin-weekly-digest-section">
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="weekly-digest-enabled-label">
        <input type="checkbox" checked={wd.enabled} onChange={(e) => setWd({ ...wd, enabled: e.target.checked })} data-testid="weekly-digest-enabled-checkbox" />
        Send the weekly "Visits by person" email
      </label>
      <p className="text-xs text-muted-foreground" data-testid="weekly-digest-lex-boundary-help">
        A manager's automatic team copy never reaches anyone at or above the final
        approver's own manager (the same boundary Approval chains and HR modules use —
        today that stops at Lex, so the Chairman never gets a team copy covering the whole
        company). Someone above that boundary only receives this email if they are added
        below as a company-wide recipient.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label>Day</Label>
          <Select value={wd.day} onValueChange={(v) => setWd({ ...wd, day: v })}>
            <SelectTrigger data-testid="weekly-digest-day-select"><SelectValue /></SelectTrigger>
            <SelectContent>{['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Hour (0-23, local)</Label>
          <Input type="number" min={0} max={23} value={wd.hour} data-testid="weekly-digest-hour-input"
            onChange={(e) => setWd({ ...wd, hour: e.target.value === '' ? '' : Number(e.target.value) })}
            onBlur={() => { if (wd.hour === '' || Number.isNaN(wd.hour)) setWd((prev) => ({ ...prev, hour: 9 })); }} />
        </div>
        <div>
          <Label>Timezone</Label>
          <Select value={wd.tz} onValueChange={(v) => setWd({ ...wd, tz: v })}>
            <SelectTrigger data-testid="weekly-digest-tz-input"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKLY_DIGEST_TZ_OPTIONS.map((tz) => (
              <SelectItem key={tz} value={tz}>{tz}</SelectItem>
            ))}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="block">Company-wide copy (sees every person, with USD)</Label>
        <p className="text-xs text-muted-foreground mb-2">Only active users with Meetings admin access or the reviewer EA role can be picked.</p>
        <div className="flex flex-wrap gap-2">
          {candidates.length === 0 ? <p className="text-xs text-muted-foreground">No eligible users found.</p> : candidates.map((c) => (
            <label key={c.id} className="flex items-center gap-1.5 border rounded-sm px-2 py-1 text-sm cursor-pointer" data-testid={`weekly-digest-recipient-${c.id}`}>
              <input type="checkbox" checked={(wd.company_wide_recipient_ids || []).includes(c.id)} onChange={() => toggleRecipient(c.id)} />
              {c.name ? `${c.name} (${c.email})` : c.email}
            </label>
          ))}
        </div>
      </div>
      <Button size="sm" disabled={saving} onClick={save} data-testid="weekly-digest-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save weekly email settings
      </Button>
    </CardContent></Card>
  );
}

// Lex's Preview feedback point 2 (26 Oct 2026): admin-editable product category list,
// replacing the stale db.categories picker. Groups (parent_code=None) and their items.
function ProductCategoriesSection() {
  const [cats, setCats] = useState([]);
  const [label, setLabel] = useState('');
  const [parentCode, setParentCode] = useState('__none__');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { meetingsAPI.adminListProductCategories().then((r) => setCats(r.data.product_categories || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const groups = cats.filter((c) => !c.parent_code);
  const childrenOf = (code) => cats.filter((c) => c.parent_code === code);

  const add = async () => {
    if (!label.trim()) { toast.error('Enter a label'); return; }
    setSaving(true);
    try {
      await meetingsAPI.adminCreateProductCategory({
        code: label.trim().toUpperCase().replace(/\s+/g, '_'), label: label.trim(),
        parent_code: parentCode === '__none__' ? null : parentCode,
      });
      setLabel(''); setParentCode('__none__'); load(); toast.success('Category added');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to add category')); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try { await meetingsAPI.adminUpdateProductCategory(c.code, { active: !c.active }); load(); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to update category')); }
  };

  const [editingCode, setEditingCode] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');

  const startEdit = (c) => { setEditingCode(c.code); setEditingLabel(c.label); };
  const saveEdit = async (code) => {
    if (!editingLabel.trim()) { toast.error('Label cannot be empty'); return; }
    try {
      await meetingsAPI.adminUpdateProductCategory(code, { label: editingLabel.trim() });
      setEditingCode(null); load(); toast.success('Label updated');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to update label')); }
  };

  const Row = ({ c, indent }) => (
    <div className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${indent ? 'pl-8 border-t' : ''}`} data-testid={`admin-category-row-${c.code}`}>
      {editingCode === c.code ? (
        <>
          <Input autoFocus className="h-8 flex-1" value={editingLabel} onChange={(e) => setEditingLabel(e.target.value)}
            data-testid={`admin-category-edit-label-input-${c.code}`}
            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(c.code); if (e.key === 'Escape') setEditingCode(null); }} />
          <Button size="sm" onClick={() => saveEdit(c.code)} data-testid={`admin-category-edit-save-${c.code}`}>Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditingCode(null)} data-testid={`admin-category-edit-cancel-${c.code}`}>Cancel</Button>
        </>
      ) : (
        <>
          <span className={`flex-1 ${indent ? '' : 'font-medium'}`}>{c.label}</span>
          <Badge variant={c.active ? 'default' : 'secondary'}>{c.active ? 'Active' : 'Inactive'}</Badge>
          <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`admin-category-edit-${c.code}`}>Edit label</Button>
          <Button size="sm" variant="outline" onClick={() => toggleActive(c)} data-testid={`admin-category-toggle-${c.code}`}>
            {c.active ? 'Deactivate' : 'Activate'}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Card className="rounded-sm"><CardContent className="p-4 space-y-4" data-testid="admin-categories-section">
      <p className="text-sm text-muted-foreground">VSTL's product list, used by the "categories"/"requirements" pointers on every report. The code is a permanent internal id and cannot be changed — only the label shown to people can be edited.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]"><Label className="text-xs">New category label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="admin-category-new-label" /></div>
        <div className="min-w-[180px]"><Label className="text-xs">Group (optional)</Label>
          <Select value={parentCode} onValueChange={setParentCode}>
            <SelectTrigger data-testid="admin-category-new-parent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Top-level (its own group)</SelectItem>
              {groups.map((g) => <SelectItem key={g.code} value={g.code}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" disabled={saving} onClick={add} data-testid="admin-category-add-btn">Add</Button>
      </div>
      <div className="divide-y border rounded-sm">
        {groups.map((g) => (
          <div key={g.code}>
            <Row c={g} indent={false} />
            {childrenOf(g.code).map((c) => <Row key={c.code} c={c} indent={true} />)}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function VisitorsSection() {
  const [visitors, setVisitors] = useState([]);
  const [name, setName] = useState('');
  const [office, setOffice] = useState('');
  const [country, setCountry] = useState('');
  const [mgrQuery, setMgrQuery] = useState('');
  const [mgrResults, setMgrResults] = useState([]);
  const [mgr, setMgr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMgrFor, setEditMgrFor] = useState(null);
  const [editMgrQuery, setEditMgrQuery] = useState('');
  const [editMgrResults, setEditMgrResults] = useState([]);
  const [userNameById, setUserNameById] = useState({});

  const load = useCallback(() => { meetingsAPI.listFieldVisitors(true).then((r) => setVisitors(r.data.field_visitors || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    userAPI.list({ status: 'active', limit: 500 }).then((r) => {
      const map = {};
      (r.data.users || []).forEach((u) => { map[u.id] = u.name; });
      setUserNameById(map);
    }).catch(() => {});
  }, []);

  const searchMgr = async (q, setResults) => {
    if (q.length < 2) { setResults([]); return; }
    try { const res = await meetingsAPI.searchColleagues(q); setResults(res.data.colleagues || []); }
    catch { setResults([]); }
  };

  const add = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await meetingsAPI.createFieldVisitor({ name: name.trim(), office: office.trim(), country: country.trim(), reports_to_user_id: mgr?.id || null });
      setName(''); setOffice(''); setCountry(''); setMgr(null); setMgrQuery(''); setMgrResults([]); load(); toast.success('Field visitor added');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to add field visitor')); }
    finally { setSaving(false); }
  };
  const toggleActive = async (v) => {
    try { await meetingsAPI.updateFieldVisitor(v.id, { active: !v.active }); load(); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to update')); }
  };
  const setReportsTo = async (v, userId) => {
    try { await meetingsAPI.updateFieldVisitor(v.id, { reports_to_user_id: userId }); setEditMgrFor(null); setEditMgrQuery(''); setEditMgrResults([]); load(); toast.success('Reports to updated'); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to update')); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-4 space-y-4" data-testid="admin-visitors-section">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
        <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-visitor-name-input" /></div>
        <div><Label className="text-xs">Office</Label><Input value={office} onChange={(e) => setOffice(e.target.value)} data-testid="admin-visitor-office-input" /></div>
        <div><Label className="text-xs">Country</Label><Input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="admin-visitor-country-input" /></div>
        <div className="relative">
          <Label className="text-xs">Reports to (optional)</Label>
          {mgr ? (
            <div className="flex items-center gap-1 text-sm border rounded-sm px-2 py-1.5" data-testid="admin-visitor-mgr-selected">
              {mgr.name}
              <button type="button" className="ml-auto text-xs text-muted-foreground" onClick={() => setMgr(null)} data-testid="admin-visitor-mgr-clear">×</button>
            </div>
          ) : (
            <Input value={mgrQuery} onChange={(e) => { setMgrQuery(e.target.value); searchMgr(e.target.value, setMgrResults); }}
              placeholder="Search manager..." data-testid="admin-visitor-mgr-input" />
          )}
          {!mgr && mgrResults.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-popover border rounded-sm shadow-md max-h-40 overflow-auto">
              {mgrResults.map((c) => (
                <button key={c.id} type="button" onClick={() => { setMgr(c); setMgrQuery(''); setMgrResults([]); }}
                  className="w-full text-left px-2 py-1 text-sm hover:bg-muted" data-testid={`admin-visitor-mgr-option-${c.id}`}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" disabled={saving} onClick={add} data-testid="admin-visitor-add-btn">Add</Button>
      </div>
      <div className="divide-y border rounded-sm">
        {visitors.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`admin-visitor-row-${v.id}`}>
            <span className="flex-1">{v.name}{v.office ? ` · ${v.office}` : ''}{v.country ? ` · ${v.country}` : ''}</span>
            {editMgrFor === v.id ? (
              <div className="relative flex items-center gap-1">
                <Input value={editMgrQuery} onChange={(e) => { setEditMgrQuery(e.target.value); searchMgr(e.target.value, setEditMgrResults); }}
                  placeholder="Search manager..." className="h-7 w-40 text-xs" data-testid={`admin-visitor-edit-mgr-input-${v.id}`} />
                <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setReportsTo(v, null)}
                  data-testid={`admin-visitor-edit-mgr-clear-${v.id}`}>Clear</button>
                <button type="button" className="text-xs text-muted-foreground" onClick={() => { setEditMgrFor(null); setEditMgrQuery(''); setEditMgrResults([]); }}
                  data-testid={`admin-visitor-edit-mgr-cancel-${v.id}`}>Cancel</button>
                {editMgrResults.length > 0 && (
                  <div className="absolute z-20 mt-1 top-full w-40 bg-popover border rounded-sm shadow-md max-h-40 overflow-auto">
                    {editMgrResults.map((c) => (
                      <button key={c.id} type="button" onClick={() => setReportsTo(v, c.id)}
                        className="w-full text-left px-2 py-1 text-xs hover:bg-muted" data-testid={`admin-visitor-edit-mgr-option-${v.id}-${c.id}`}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Badge variant="outline" className="text-[10px] cursor-pointer" onClick={() => { setEditMgrFor(v.id); setEditMgrQuery(''); setEditMgrResults([]); }}
                data-testid={`admin-visitor-reports-to-${v.id}`}>
                Reports to: {v.reports_to_user_id ? (userNameById[v.reports_to_user_id] || v.reports_to_user_id) : 'none (anyone can pick)'}
              </Badge>
            )}
            <Badge variant={v.active ? 'secondary' : 'outline'} className="text-[10px]">{v.active ? 'Active' : 'Inactive'}</Badge>
            <Button size="sm" variant="ghost" onClick={() => toggleActive(v)} data-testid={`admin-visitor-toggle-${v.id}`}>
              {v.active ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        ))}
        {visitors.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">No field visitors yet.</p>}
      </div>
    </CardContent></Card>
  );
}

function QuestionsSection() {
  const [questions, setQuestions] = useState([]);
  const load = useCallback(() => { meetingsAPI.adminListQuestions().then((r) => setQuestions(r.data.questions || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (q) => {
    if (q.is_system && q.active) { toast.error('A system question cannot be deactivated'); return; }
    try { await meetingsAPI.adminUpdateQuestion(q.id, { active: !q.active }); load(); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to update question')); }
  };
  const editLabel = async (q) => {
    const label = window.prompt('New label:', q.label);
    if (!label || label === q.label) return;
    try { await meetingsAPI.adminUpdateQuestion(q.id, { label }); toast.success('Saved — version bumped'); load(); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to update question')); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-4" data-testid="admin-questions-section">
      <div className="divide-y border rounded-sm">
        {questions.map((q) => (
          <div key={q.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`admin-question-row-${q.id}`}>
            <span className="w-full sm:w-24 text-xs text-muted-foreground">{q.section}</span>
            <span className="flex-1 min-w-[140px]">{q.label} <span className="text-xs text-muted-foreground">({q.code}, v{q.version || 1})</span></span>
            {q.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
            <Badge variant={q.active !== false ? 'secondary' : 'outline'} className="text-[10px]">{q.active !== false ? 'Active' : 'Inactive'}</Badge>
            <Button size="sm" variant="ghost" onClick={() => editLabel(q)} data-testid={`admin-question-edit-${q.id}`}>Edit label</Button>
            <Button size="sm" variant="ghost" onClick={() => toggleActive(q)} data-testid={`admin-question-toggle-${q.id}`}>
              {q.active !== false ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function ProspectsSection() {
  const [prospects, setProspects] = useState([]);
  const [matchType, setMatchType] = useState({});
  const [matchQuery, setMatchQuery] = useState({});
  const [matchResults, setMatchResults] = useState({});
  const [matchSelected, setMatchSelected] = useState({});
  const timers = useRef({});
  const requestIds = useRef({});
  const load = useCallback(() => { meetingsAPI.adminListProspects('UNMATCHED').then((r) => setProspects(r.data.prospects || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const typeFor = (p) => matchType[p.id] || (p.potential_type === 'BOTH' ? 'VENDOR' : p.potential_type);

  const search = (p, q) => {
    setMatchQuery((prev) => ({ ...prev, [p.id]: q }));
    if (timers.current[p.id]) clearTimeout(timers.current[p.id]);
    if (q.length < 2) { setMatchResults((prev) => ({ ...prev, [p.id]: [] })); return; }
    const myId = (requestIds.current[p.id] = (requestIds.current[p.id] || 0) + 1);
    timers.current[p.id] = setTimeout(async () => {
      try {
        const res = await meetingsAPI.searchCompanies(q);
        if (requestIds.current[p.id] !== myId) return;
        setMatchResults((prev) => ({ ...prev, [p.id]: (res.data.companies || []).filter((c) => c.type === typeFor(p)) }));
      } catch { if (requestIds.current[p.id] === myId) setMatchResults((prev) => ({ ...prev, [p.id]: [] })); }
    }, 250);
  };

  const match = async (p) => {
    const sel = matchSelected[p.id];
    if (!sel) { toast.error('Search and pick the matching company first'); return; }
    try {
      await meetingsAPI.adminMatchProspect(p.id, { matched_type: typeFor(p), matched_ref: sel.ref });
      toast.success('Matched'); load();
    } catch (e) { toast.error(apiErrorText(e, 'Failed to match')); }
  };
  const keep = async (p) => {
    try { await meetingsAPI.adminKeepAsProspect(p.id); toast.success('Kept as prospect'); load(); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to update')); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-4 space-y-2" data-testid="admin-prospects-section">
      {prospects.length === 0 && <p className="text-sm text-muted-foreground">No unmatched prospects.</p>}
      {prospects.map((p) => (
        <div key={p.id} className="border rounded-sm px-3 py-2 text-sm space-y-2" data-testid={`admin-prospect-row-${p.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex-1">{p.display_name} <span className="text-xs text-muted-foreground">({p.country}, potential {p.potential_type})</span></span>
            {p.potential_type === 'BOTH' && (
              <div className="flex gap-1">
                {['VENDOR', 'CUSTOMER'].map((t) => (
                  <button key={t} type="button" onClick={() => setMatchType((prev) => ({ ...prev, [p.id]: t }))} data-testid={`admin-prospect-type-${t}-${p.id}`}
                    className={`px-2 py-0.5 text-xs rounded-sm border ${typeFor(p) === t ? 'bg-foreground text-background border-foreground' : 'bg-background'}`}>{t}</button>
                ))}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={() => keep(p)} data-testid={`admin-prospect-keep-btn-${p.id}`}>Keep as prospect</Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input className="w-64" placeholder={`Search real ${typeFor(p).toLowerCase()} to match...`}
              value={matchSelected[p.id] ? matchSelected[p.id].name : (matchQuery[p.id] || '')}
              onChange={(e) => { setMatchSelected((prev) => ({ ...prev, [p.id]: null })); search(p, e.target.value); }}
              data-testid={`admin-prospect-match-search-${p.id}`} />
            <Button size="sm" disabled={!matchSelected[p.id]} onClick={() => match(p)} data-testid={`admin-prospect-match-btn-${p.id}`}>Match</Button>
          </div>
          {(matchResults[p.id] || []).length > 0 && !matchSelected[p.id] && (
            <div className="border rounded-sm divide-y max-h-32 overflow-y-auto">
              {matchResults[p.id].map((c) => (
                <button key={c.ref} type="button" className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/60"
                  onClick={() => { setMatchSelected((prev) => ({ ...prev, [p.id]: c })); setMatchResults((prev) => ({ ...prev, [p.id]: [] })); }}
                  data-testid={`admin-prospect-match-option-${c.ref}`}>{c.name}</button>
              ))}
            </div>
          )}
        </div>
      ))}
    </CardContent></Card>
  );
}

function ColleaguePicker({ value, onChange, testId, includeInactive }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);
  const search = (q) => {
    setQuery(q); onChange(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) { setResults([]); return; }
    const myId = ++requestIdRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await meetingsAPI.searchColleagues(q, includeInactive);
        if (requestIdRef.current === myId) setResults(res.data.colleagues || []);
      } catch { if (requestIdRef.current === myId) setResults([]); }
    }, 250);
  };
  return (
    <div>
      <Input value={value ? value.name : query} onChange={(e) => search(e.target.value)} data-testid={testId} />
      {results.length > 0 && !value && (
        <div className="border rounded-sm divide-y max-h-32 overflow-y-auto mt-1">
          {results.map((c) => (
            <button key={c.id} type="button" className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/60"
              onClick={() => { onChange(c); setResults([]); }} data-testid={`${testId}-option-${c.id}`}>
              {c.name}{c.email ? <span className="text-muted-foreground"> — {c.email}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReassignSection() {
  const [fromUser, setFromUser] = useState(null);
  const [toUser, setToUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!fromUser || !toUser) { toast.error('Pick both a from and a to colleague'); return; }
    if (fromUser.id === toUser.id) { toast.error('From and to must be different people'); return; }
    setSaving(true); setResult(null);
    try {
      const res = await meetingsAPI.adminReassign({ from_user_id: fromUser.id, to_user_id: toUser.id });
      setResult(res.data); toast.success('Reassigned');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to reassign')); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-sm"><CardContent className="p-4 space-y-3" data-testid="admin-reassign-section">
      <p className="text-sm text-muted-foreground">Move someone's open meetings and open action items to another active Meetings user — e.g. when they leave.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">From (any user, including inactive)</Label><ColleaguePicker value={fromUser} onChange={setFromUser} testId="admin-reassign-from-input" includeInactive /></div>
        <div><Label className="text-xs">To (active Meetings user)</Label><ColleaguePicker value={toUser} onChange={setToUser} testId="admin-reassign-to-input" /></div>
      </div>
      <Button size="sm" disabled={saving || !fromUser || !toUser || fromUser.id === toUser.id} onClick={run} data-testid="admin-reassign-run-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Reassign
      </Button>
      {result && <p className="text-sm" data-testid="admin-reassign-result">Moved {result.meetings_reassigned} meetings and {result.actions_reassigned} action items.</p>}
    </CardContent></Card>
  );
}
